import { useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { useLanguage } from '../i18n';
import { befelsApi } from '../services/api';
import type { BulkOrderItem, BulkResultItem } from '../types';
import styles from './BulkUploadModal.module.css';

interface BulkUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  platform: string;
  token: string;
  onSuccess?: () => void;  // Callback to refresh data after successful upload
}

type ModalTab = 'download' | 'upload';
type UploadState = 'idle' | 'parsing' | 'uploading' | 'done';

interface ParsedRow {
  rowNum: number;
  barkod: string;
  altFiyat: number;
  ustFiyat: number;
  paraBirimi: number;
  emirTipi: number;
  priceIncludesCommission: boolean;
  activateCoupon: boolean;
  activatePromotion: boolean;
  parseError?: string;
}

// Required columns in the Excel template
const REQUIRED_COLUMNS = ['Barkod', 'Alt Fiyat', 'Üst Fiyat', 'Emir Tipi'];

// Security constants
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_STRING_LENGTH = 500;
const MAX_ROWS = 10100;
const MAX_PRICE = 1_000_000; // Maximum price value (1 million TRY)

/**
 * Sanitize string value to prevent formula injection and other attacks.
 * Excel formulas start with =, +, -, @, tab, or carriage return.
 */
function sanitizeString(value: unknown): string {
  if (value === null || value === undefined) return '';

  let str = String(value).trim();

  // Remove potential formula injection characters at start
  // These can trigger Excel formula execution: = + - @ \t \r
  // Also remove leading single quote (') - Excel uses this to mark text cells
  while (str.length > 0 && /^[=+\-@\t\r\n']/.test(str)) {
    str = str.substring(1).trim();
  }

  // Remove any control characters
  str = str.replace(/[\x00-\x1F\x7F]/g, '');

  // Limit length
  if (str.length > MAX_STRING_LENGTH) {
    str = str.substring(0, MAX_STRING_LENGTH);
  }

  return str;
}

/**
 * Safely parse numeric value, rejecting suspicious strings.
 * Returns 0 for invalid values and caps at MAX_PRICE.
 */
function safeParseNumber(value: unknown, maxValue: number = MAX_PRICE): number {
  let num: number;

  if (typeof value === 'number') {
    num = isFinite(value) ? value : 0;
  } else {
    const str = sanitizeString(value);
    num = parseFloat(str);
    num = isFinite(num) ? num : 0;
  }

  // Cap at max value to prevent unreasonable values
  if (num > maxValue) {
    num = maxValue;
  }

  return num;
}

export function BulkUploadModal({ isOpen, onClose, platform, token, onSuccess }: BulkUploadModalProps) {
  const { t } = useLanguage();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State
  const [activeTab, setActiveTab] = useState<ModalTab>('download');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [parseError, setParseError] = useState<string | null>(null);
  const [results, setResults] = useState<BulkResultItem[]>([]);
  const [errorCount, setErrorCount] = useState(0);
  const [createdCount, setCreatedCount] = useState(0);
  const [updatedCount, setUpdatedCount] = useState(0);
  const [unchangedCount, setUnchangedCount] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Reset state when modal closes
  const handleClose = useCallback(() => {
    setSelectedFile(null);
    setUploadState('idle');
    setParseError(null);
    setResults([]);
    setErrorCount(0);
    setCreatedCount(0);
    setUpdatedCount(0);
    setUnchangedCount(0);
    setActiveTab('download');
    onClose();
  }, [onClose]);

  // Generate and download template Excel file
  const handleDownloadTemplate = useCallback(() => {
    // Create workbook with template structure
    const wb = XLSX.utils.book_new();

    // Header row with column names (Description first - optional, for user reference only)
    const headers = [
      t.bulkUpload.columnDescription,
      t.bulkUpload.columnBarcode,
      t.bulkUpload.columnMinPrice,
      t.bulkUpload.columnMaxPrice,
      t.bulkUpload.columnCurrency,
      t.bulkUpload.columnOrderType,
      t.bulkUpload.columnCommissionIncluded,
      t.bulkUpload.columnCouponActive,
      t.bulkUpload.columnPromotionActive,
    ];

    // Example row
    const exampleRow = [
      'Örnek Ürün Adı',  // Description (optional, not processed)
      '8682826088022',   // Example barcode
      2500,              // Min price
      4000,              // Max price
      0,                 // Currency (TRY)
      0,                 // Order type (BuyBox)
      1,                 // Commission included (1=yes, 0=no)
      0,                 // Coupon active (0=no, 1=yes)
      0,                 // Promotion active (0=no, 1=yes)
    ];

    const wsData = [headers, exampleRow];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Set column widths
    ws['!cols'] = [
      { wch: 30 },  // Description (optional)
      { wch: 20 },  // Barcode
      { wch: 15 },  // Min Price
      { wch: 15 },  // Max Price
      { wch: 40 },  // Currency (with description)
      { wch: 35 },  // Order Type (with description)
      { wch: 20 },  // Commission Included
      { wch: 15 },  // Coupon Active
      { wch: 15 },  // Promotion Active
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Orders');

    // Download file
    XLSX.writeFile(wb, t.bulkUpload.templateFilename);
  }, [t]);

  // Handle file selection
  const handleFileSelect = useCallback((file: File) => {
    // Validate file type
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      setParseError(t.bulkUpload.invalidFormat);
      setSelectedFile(null);
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setParseError(t.bulkUpload.fileTooLarge);
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
    setParseError(null);
    setResults([]);
  }, [t]);

  // Handle file input change
  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  // Handle drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  // Parse Excel file
  const parseExcelFile = useCallback(async (file: File): Promise<ParsedRow[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });

          // Get first sheet
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];

          // Convert to JSON with header row
          const jsonData = XLSX.utils.sheet_to_json<Record<string, unknown>>(worksheet, { defval: '' });

          if (jsonData.length === 0) {
            reject(new Error(t.bulkUpload.emptyFile));
            return;
          }

          // Security: limit number of rows
          if (jsonData.length > MAX_ROWS) {
            reject(new Error(`Maksimum ${MAX_ROWS} satır yüklenebilir`));
            return;
          }

          // Check for required columns (check first row keys)
          const firstRow = jsonData[0];
          const columnKeys = Object.keys(firstRow);

          // Normalize column names for matching
          const normalizeCol = (col: string) => col.toLowerCase().replace(/\s+/g, '').replace(/[()]/g, '');
          const normalizedKeys = columnKeys.map(normalizeCol);

          const missingCols = REQUIRED_COLUMNS.filter(reqCol => {
            const normalized = normalizeCol(reqCol);
            return !normalizedKeys.some(key => key.includes(normalized) || normalized.includes(key));
          });

          if (missingCols.length > 0) {
            reject(new Error(t.bulkUpload.missingColumns.replace('{columns}', missingCols.join(', '))));
            return;
          }

          // Parse each row
          const parsedRows: ParsedRow[] = [];

          for (let i = 0; i < jsonData.length; i++) {
            const row = jsonData[i];
            const rowNum = i + 2; // +2 because row 1 is header, and arrays are 0-indexed

            // Find column values (flexible column name matching)
            const findValue = (keywords: string[]): unknown => {
              for (const key of columnKeys) {
                const normalizedKey = normalizeCol(key);
                if (keywords.some(kw => normalizedKey.includes(normalizeCol(kw)))) {
                  return row[key];
                }
              }
              return undefined;
            };

            // Note: "Ürün Açıklaması" column is intentionally NOT processed - it's for user reference only

            // Use sanitizeString for string values to prevent injection attacks
            const barkod = sanitizeString(findValue(['barkod', 'barcode']));
            const altFiyatRaw = findValue(['altfiyat', 'minprice', 'alt']);
            const ustFiyatRaw = findValue(['üstfiyat', 'ustfiyat', 'maxprice', 'üst', 'ust']);
            const paraBirimiRaw = findValue(['parabirimi', 'currency', 'para']);
            const emirTipiRaw = findValue(['emirtipi', 'ordertype', 'emir']);
            const commissionIncludedRaw = findValue(['komisyondahil', 'commissionincluded', 'komisyon']);
            const couponActiveRaw = findValue(['kuponaktif', 'couponactive', 'kupon']);
            const promotionActiveRaw = findValue(['indirimaktif', 'promotionactive', 'indirim']);

            // Parse numeric values using safe parser
            const altFiyat = safeParseNumber(altFiyatRaw);
            const ustFiyat = safeParseNumber(ustFiyatRaw);
            const paraBirimi = Math.floor(safeParseNumber(paraBirimiRaw));
            const emirTipi = Math.floor(safeParseNumber(emirTipiRaw));
            // Default to true (commission included) if not specified or value is 1/true
            const priceIncludesCommission = commissionIncludedRaw === undefined ||
              commissionIncludedRaw === '' ||
              commissionIncludedRaw === 1 ||
              commissionIncludedRaw === '1' ||
              commissionIncludedRaw === true ||
              String(commissionIncludedRaw).toLowerCase() === 'true';
            // Default to false for coupon/promotion if not specified
            const activateCoupon = couponActiveRaw === 1 ||
              couponActiveRaw === '1' ||
              couponActiveRaw === true ||
              String(couponActiveRaw).toLowerCase() === 'true';
            const activatePromotion = promotionActiveRaw === 1 ||
              promotionActiveRaw === '1' ||
              promotionActiveRaw === true ||
              String(promotionActiveRaw).toLowerCase() === 'true';

            // Basic validation
            let parseError: string | undefined;
            if (!barkod) {
              parseError = t.bulkUpload.errors.MISSING_BARCODE;
            } else if (altFiyat <= 0 || ustFiyat <= 0) {
              parseError = t.bulkUpload.errors.INVALID_PRICE;
            } else if (altFiyat >= ustFiyat) {
              parseError = t.bulkUpload.errors.INVALID_PRICE;
            }

            parsedRows.push({
              rowNum,
              barkod,
              altFiyat,
              ustFiyat,
              paraBirimi: Math.max(0, Math.min(3, paraBirimi)), // Clamp to 0-3
              emirTipi: Math.max(0, Math.min(1, emirTipi)), // Clamp to 0-1
              priceIncludesCommission,
              activateCoupon,
              activatePromotion,
              parseError,
            });
          }

          resolve(parsedRows);
        } catch {
          reject(new Error(t.bulkUpload.emptyFile));
        }
      };

      reader.onerror = () => {
        reject(new Error(t.bulkUpload.emptyFile));
      };

      reader.readAsArrayBuffer(file);
    });
  }, [t]);

  // Handle upload and validate
  const handleUploadAndValidate = useCallback(async () => {
    if (!selectedFile) {
      setParseError(t.bulkUpload.noFile);
      return;
    }

    setUploadState('parsing');
    setParseError(null);

    try {
      // Parse Excel file
      const parsedRows = await parseExcelFile(selectedFile);

      // Separate valid rows from parse errors
      const validRows = parsedRows.filter(row => !row.parseError);
      const parseErrorRows = parsedRows.filter(row => row.parseError);

      // If no valid rows, show parse errors
      if (validRows.length === 0 && parseErrorRows.length > 0) {
        setParseError(t.bulkUpload.emptyFile);
        setUploadState('idle');
        return;
      }

      setUploadState('uploading');

      // Prepare orders for API
      const orders: BulkOrderItem[] = validRows.map(row => ({
        barkod: row.barkod,
        altFiyat: row.altFiyat,
        ustFiyat: row.ustFiyat,
        paraBirimi: row.paraBirimi,
        emirTipi: row.emirTipi,
        priceIncludesCommission: row.priceIncludesCommission,
        activateCoupon: row.activateCoupon,
        activatePromotion: row.activatePromotion,
      }));

      // Call bulk create API
      const response = await befelsApi.bulkCreate(token, platform, orders);

      if (response.error) {
        setParseError(response.error);
        setUploadState('idle');
        return;
      }

      if (response.data) {
        // Combine API results with parse errors
        const apiResults = response.data.results;

        // Add parse error rows to results
        const allResults: BulkResultItem[] = [
          ...parseErrorRows.map(row => ({
            row: row.rowNum,
            barkod: row.barkod,
            success: false,
            error: row.parseError,
          })),
          ...apiResults,
        ];

        // Sort by row number
        allResults.sort((a, b) => a.row - b.row);

        setResults(allResults);
        setErrorCount(response.data.errorCount + parseErrorRows.length);
        setCreatedCount(response.data.createdCount || 0);
        setUpdatedCount(response.data.updatedCount || 0);
        setUnchangedCount(response.data.unchangedCount || 0);
        setUploadState('done');

        // Call success callback if there were any successful uploads
        if (response.data.successCount > 0 && onSuccess) {
          onSuccess();
        }
      }
    } catch (error) {
      setParseError(error instanceof Error ? error.message : t.bulkUpload.emptyFile);
      setUploadState('idle');
    }
  }, [selectedFile, parseExcelFile, token, platform, t, onSuccess]);

  // Download error rows as Excel
  const handleDownloadErrors = useCallback(() => {
    const errorRows = results.filter(r => !r.success);
    if (errorRows.length === 0) return;

    const wb = XLSX.utils.book_new();

    // Create data with error and note columns
    const headers = [
      'Satır',
      t.bulkUpload.columnBarcode,
      'Hata',
      'Not',
    ];

    const data = errorRows.map(row => [
      row.row,
      row.barkod,
      row.error ? (t.bulkUpload.errors[row.error as keyof typeof t.bulkUpload.errors] || row.error) : '',
      row.note || '',  // Include note from backend (Turkish price warning message)
    ]);

    const wsData = [headers, ...data];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    ws['!cols'] = [
      { wch: 8 },   // Satır
      { wch: 20 },  // Barkod
      { wch: 40 },  // Hata
      { wch: 80 },  // Not (wider for the Turkish message)
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Errors');
    XLSX.writeFile(wb, 'hatali_satirlar.xlsx');
  }, [results, t]);

  // Translate error code
  const translateError = useCallback((errorCode: string | undefined): string => {
    if (!errorCode) return '';
    const translated = t.bulkUpload.errors[errorCode as keyof typeof t.bulkUpload.errors];
    return translated || errorCode;
  }, [t]);

  // Reset to try again
  const handleTryAgain = useCallback(() => {
    setSelectedFile(null);
    setUploadState('idle');
    setParseError(null);
    setResults([]);
    setErrorCount(0);
    setCreatedCount(0);
    setUpdatedCount(0);
    setUnchangedCount(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <h2>{t.bulkUpload.title}</h2>
          <button className={styles.closeBtn} onClick={handleClose}>
            &times;
          </button>
        </div>

        {/* Tabs */}
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'download' ? styles.active : ''}`}
            onClick={() => setActiveTab('download')}
          >
            {t.bulkUpload.downloadTemplate}
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'upload' ? styles.active : ''}`}
            onClick={() => setActiveTab('upload')}
          >
            {t.bulkUpload.uploadFile}
          </button>
        </div>

        {/* Body */}
        <div className={styles.modalBody}>
          {activeTab === 'download' && (
            <div className={styles.downloadTab}>
              <p className={styles.description}>
                Excel şablonunu indirin, ürün bilgilerini doldurun ve "Dosya Yükle" sekmesinden yükleyin.
              </p>

              <div className={styles.templateInfo}>
                <h4>Şablon Sütunları:</h4>
                <ul>
                  <li><strong>Ürün Açıklaması</strong> - Ürün ismi veya notu (opsiyonel, işlenmez - sadece takip için)</li>
                  <li><strong>Barkod</strong> - Ürün barkodu (zorunlu)</li>
                  <li><strong>Alt Fiyat</strong> - Minimum fiyat (zorunlu)</li>
                  <li><strong>Üst Fiyat</strong> - Maksimum fiyat (zorunlu)</li>
                  <li><strong>Para Birimi</strong> - 0=TRY, 1=USD, 2=EUR, 3=CNY (varsayılan: 0)</li>
                  <li><strong>Emir Tipi</strong> - 0=BuyBox, 1=Baskı (zorunlu)</li>
                  <li><strong>Komisyon Dahil</strong> - 1=Evet, 0=Hayır (varsayılan: 1)</li>
                  <li><strong>Kupon Aktif</strong> - 1=Evet, 0=Hayır (varsayılan: 0)</li>
                  <li><strong>İndirim Aktif</strong> - 1=Evet, 0=Hayır (varsayılan: 0)</li>
                </ul>
              </div>

              <button className={styles.downloadBtn} onClick={handleDownloadTemplate}>
                {t.bulkUpload.downloadTemplate}
              </button>
            </div>
          )}

          {activeTab === 'upload' && (
            <div className={styles.uploadTab}>
              {uploadState === 'idle' && (
                <>
                  {/* Drop zone */}
                  <div
                    className={`${styles.dropzone} ${isDragging ? styles.dragging : ''} ${selectedFile ? styles.hasFile : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileInputChange}
                      className={styles.fileInput}
                    />

                    {selectedFile ? (
                      <div className={styles.selectedFileInfo}>
                        <span className={styles.fileIcon}>📊</span>
                        <span className={styles.fileName}>{selectedFile.name}</span>
                      </div>
                    ) : (
                      <div className={styles.dropzoneContent}>
                        <span className={styles.uploadIcon}>📁</span>
                        <p>{isDragging ? t.bulkUpload.dropzoneActive : t.bulkUpload.dropzone}</p>
                      </div>
                    )}
                  </div>

                  {/* Error message */}
                  {parseError && (
                    <div className={styles.errorMessage}>{parseError}</div>
                  )}

                  {/* Upload button */}
                  <button
                    className={styles.uploadBtn}
                    onClick={handleUploadAndValidate}
                    disabled={!selectedFile}
                  >
                    {t.bulkUpload.uploadAndValidate}
                  </button>
                </>
              )}

              {(uploadState === 'parsing' || uploadState === 'uploading') && (
                <div className={styles.loadingState}>
                  <div className={styles.spinner}></div>
                  <p>{uploadState === 'parsing' ? t.bulkUpload.validating : t.bulkUpload.validating}</p>
                </div>
              )}

              {uploadState === 'done' && (
                <div className={styles.resultsState}>
                  <h3>{t.bulkUpload.resultsTitle}</h3>

                  {/* Summary */}
                  <div className={styles.summary}>
                    {createdCount > 0 && (
                      <div className={styles.successSummary}>
                        {t.bulkUpload.createdCount.replace('{count}', String(createdCount))}
                      </div>
                    )}
                    {updatedCount > 0 && (
                      <div className={styles.updatedSummary}>
                        {t.bulkUpload.updatedCount.replace('{count}', String(updatedCount))}
                      </div>
                    )}
                    {unchangedCount > 0 && (
                      <div className={styles.unchangedSummary}>
                        {t.bulkUpload.unchangedCount.replace('{count}', String(unchangedCount))}
                      </div>
                    )}
                    {errorCount > 0 && (
                      <div className={styles.errorSummary}>
                        {t.bulkUpload.errorCount.replace('{count}', String(errorCount))}
                      </div>
                    )}
                  </div>

                  {/* Results table - exclude UNCHANGED items (only show in summary) */}
                  {(() => {
                    const displayResults = results.filter(r => r.action !== 'UNCHANGED');
                    return displayResults.length > 0 && (
                      <div className={styles.resultsTableWrapper}>
                        <table className={styles.resultsTable}>
                          <thead>
                            <tr>
                              <th>Satır</th>
                              <th>Barkod</th>
                              <th>Durum</th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayResults.slice(0, 50).map((result, idx) => {
                              // Determine row style based on action
                              const rowClass = result.success
                                ? (result.action === 'UPDATED' ? styles.updatedRow : styles.successRow)
                                : styles.errorRow;

                              return (
                                <tr key={idx} className={rowClass}>
                                  <td>{result.row}</td>
                                  <td>{result.barkod}</td>
                                  <td>
                                    {result.success ? (
                                      <span className={result.action === 'UPDATED' ? styles.updatedBadge : styles.successBadge}>
                                        {result.action === 'UPDATED' ? t.bulkUpload.actionUpdated : t.bulkUpload.actionCreated}
                                      </span>
                                    ) : (
                                      <span className={styles.errorText}>{translateError(result.error)}</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                            {displayResults.length > 50 && (
                              <tr>
                                <td colSpan={3} className={styles.moreRows}>
                                  ... ve {displayResults.length - 50} satır daha
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}

                  {/* Action buttons */}
                  <div className={styles.resultActions}>
                    {errorCount > 0 && (
                      <button className={styles.downloadErrorsBtn} onClick={handleDownloadErrors}>
                        {t.bulkUpload.downloadErrors}
                      </button>
                    )}
                    <button className={styles.tryAgainBtn} onClick={handleTryAgain}>
                      {t.bulkUpload.tryAgain}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.modalFooter}>
          <button className={styles.cancelBtn} onClick={handleClose}>
            {t.bulkUpload.close}
          </button>
        </div>
      </div>
    </div>
  );
}
